import { useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  LayoutDashboard,
  MailCheck,
  Radio,
  ShieldCheck,
  UserCog,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { AdminErrorState } from "@/components/admin/AdminDataState";
import PartnerForm, { type PartnerFormValues } from "@/components/forms/PartnerForm";
import { PageHeader } from "@/components/layouts/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import {
  fetchAdminPartner,
  updateAdminPartner,
  type AdminPartnerRecord,
} from "@/api/adminPartner";
import { usePartners } from "@/context/PartnerContext";

type StatusTone = "success" | "warning" | "info" | "neutral";

const statusTone: Record<StatusTone, string> = {
  success: "border-emerald-200 bg-emerald-50/70 text-emerald-800",
  warning: "border-amber-200 bg-amber-50/70 text-amber-900",
  info: "border-sky-200 bg-sky-50/70 text-sky-900",
  neutral: "border-slate-200 bg-slate-50 text-slate-700",
};

function PartnerStatusCard({
  icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
  tone: StatusTone;
}) {
  return (
    <div className={`min-w-0 rounded-2xl border p-3.5 sm:p-4 ${statusTone[tone]}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] opacity-75">{icon}{label}</div>
      <p className="mt-2 truncate text-base font-bold text-slate-950">{value}</p>
      <p className="mt-1 text-xs leading-5 opacity-80">{detail}</p>
    </div>
  );
}

function PartnerEditorSkeleton() {
  return (
    <div className="space-y-5" aria-label="Φόρτωση συνεργάτη">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">{Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-28 rounded-2xl" />)}</div>
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.08fr)_minmax(360px,0.92fr)]"><div className="space-y-5"><Skeleton className="h-[390px] rounded-2xl" /><Skeleton className="h-56 rounded-2xl" /></div><Skeleton className="h-[560px] rounded-2xl" /></div>
    </div>
  );
}

const EditPartner = () => {
  const { id = "" } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { refreshPartners } = usePartners();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: ["admin-partner", id],
    queryFn: () => fetchAdminPartner(id),
    enabled: id !== "",
    staleTime: 30_000,
  });

  const partner = query.data;
  const attention = useMemo(() => {
    if (!partner || !partner.active) return [];
    const items: string[] = [];
    if (partner.services.length === 0) items.push("Δεν έχει επιλεγμένη υπηρεσία routing.");
    if (partner.latitude === null || partner.longitude === null) items.push("Δεν έχει ακριβές σημείο στον χάρτη.");
    if (partner.radius <= 0) items.push("Δεν έχει έγκυρη ακτίνα κάλυψης.");
    if (!partner.emailReady && !partner.smsReady) items.push("Δεν υπάρχει ενεργό κανάλι ειδοποίησης.");
    return items;
  }, [partner]);

  const savePartner = async (values: PartnerFormValues) => {
    const saved = await updateAdminPartner(id, {
      name: values.name.trim(),
      phone: values.phone.trim(),
      email: values.email.trim(),
      location: values.location.trim(),
      miniImageUrl: values.miniImageUrl?.trim() || null,
      miniDescription: values.miniDescription?.trim() || null,
      services: values.services as AdminPartnerRecord["services"],
      active: values.active,
      smsOptOut: values.sms_opt_out,
      latitude: values.latitude ?? null,
      longitude: values.longitude ?? null,
      radius: values.radius,
    });
    queryClient.setQueryData(["admin-partner", id], saved);
    await refreshPartners();
    toast({ title: "Οι αλλαγές αποθηκεύτηκαν", description: `${saved.name} ενημερώθηκε χωρίς αλλαγή στις ιστορικές αναθέσεις.` });
  };

  const initialData = partner ? {
    id: partner.id,
    name: partner.name,
    phone: partner.phone,
    email: partner.email,
    location: partner.location,
    miniImageUrl: partner.miniImageUrl || "",
    miniDescription: partner.miniDescription || "",
    services: partner.services,
    active: partner.active,
    sms_opt_out: partner.smsOptOut,
    latitude: partner.latitude,
    longitude: partner.longitude,
    radius: partner.radius,
  } : undefined;

  const updatedLabel = partner?.updatedAt
    ? format(parseISO(partner.updatedAt), "d MMM yyyy, HH:mm", { locale: el })
    : "Χωρίς καταγεγραμμένη ενημέρωση";

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        eyebrow="Δίκτυο συνεργατών · Ρυθμίσεις"
        title={partner?.name || (query.isLoading ? "Φόρτωση συνεργάτη…" : "Επεξεργασία συνεργάτη")}
        description={partner ? `${partner.location || "Χωρίς περιοχή"} · Τελευταία ενημέρωση ${updatedLabel}` : "Ασφαλής ενημέρωση routing, επικοινωνίας και περιοχής κάλυψης."}
        icon={<UserCog />}
        iconGradient="blue"
        actions={
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
            <Button asChild variant="outline" className="h-11 gap-2"><Link to="/admin/partners"><ArrowLeft className="h-4 w-4" /> Συνεργάτες</Link></Button>
            {partner && <Button asChild className="h-11 gap-2 bg-slate-950 text-white hover:bg-slate-800"><Link to={`/admin/partners/${encodeURIComponent(partner.id)}/dashboard`}><LayoutDashboard className="h-4 w-4" /> Dashboard</Link></Button>}
          </div>
        }
      />

      {!id ? (
        <AdminErrorState message="Δεν έχει οριστεί αναγνωριστικό συνεργάτη." />
      ) : query.isLoading ? (
        <PartnerEditorSkeleton />
      ) : query.isError || !partner ? (
        <AdminErrorState message={query.error instanceof Error ? query.error.message : "Ο συνεργάτης δεν βρέθηκε."} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <section aria-label="Ετοιμότητα συνεργάτη" className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <PartnerStatusCard icon={partner.routingReady ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} label="Routing" value={partner.active ? (partner.routingReady ? "Έτοιμος" : "Χρειάζεται ρύθμιση") : "Ανενεργός"} detail={partner.active ? `${partner.services.length} υπηρεσίες · ${partner.radius} χλμ.` : "Εξαιρείται από νέες αναθέσεις"} tone={partner.routingReady ? "success" : partner.active ? "warning" : "neutral"} />
            <PartnerStatusCard icon={<MailCheck className="h-4 w-4" />} label="Ειδοποιήσεις" value={partner.emailReady && partner.smsReady ? "Email + SMS" : partner.emailReady ? "Μόνο email" : partner.smsReady ? "Μόνο SMS" : "Χωρίς κανάλι"} detail={partner.smsOptOut ? "SMS opt-out ενεργό" : "Με βάση τα αποθηκευμένα στοιχεία"} tone={partner.emailReady || partner.smsReady ? "info" : "warning"} />
            <PartnerStatusCard icon={<ShieldCheck className="h-4 w-4" />} label="Portal" value={partner.portalReady ? "Έχει κωδικό" : "Δεν έχει συνδεθεί"} detail={partner.lastLoginAt ? `Τελευταία είσοδος ${format(parseISO(partner.lastLoginAt), "d MMM, HH:mm", { locale: el })}` : "Τα secure lead links συνεχίζουν χωρίς login"} tone={partner.portalReady ? "success" : "neutral"} />
            <PartnerStatusCard icon={<Activity className="h-4 w-4" />} label="Ροή leads" value={`${new Intl.NumberFormat("el-GR").format(partner.leads30d)} στις 30 ημέρες`} detail={`${new Intl.NumberFormat("el-GR").format(partner.totalLeads)} συνολικά ανατεθειμένα`} tone="info" />
          </section>

          {attention.length > 0 && (
            <Card className="rounded-2xl border-amber-200 bg-amber-50/70 shadow-none">
              <CardContent className="flex gap-3 p-4 sm:p-5"><AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" /><div><h2 className="text-sm font-semibold text-amber-950">Πριν λάβει νέα leads</h2><ul className="mt-2 space-y-1 text-sm leading-6 text-amber-900">{attention.map((item) => <li key={item}>• {item}</li>)}</ul></div></CardContent>
            </Card>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm sm:p-4">
            <div className="flex min-w-0 items-center gap-3"><div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700"><Radio className="h-5 w-5" /></div><div className="min-w-0"><p className="text-sm font-semibold text-slate-950">Αλλαγές μόνο για τα επόμενα leads</p><p className="mt-0.5 text-xs leading-5 text-slate-500">Οι υπάρχουσες αναθέσεις και οι ήδη σταλμένες ειδοποιήσεις παραμένουν αμετάβλητες.</p></div></div>
            <Button type="button" variant="outline" className="h-11 w-full gap-2 sm:w-auto" onClick={() => window.open(`/mini-preview/${partner.id}`, "_blank", "noopener,noreferrer")}><ExternalLink className="h-4 w-4" /> Mini page</Button>
          </div>

          <PartnerForm initialData={initialData} isAdminView submitPartner={savePartner} submitLabel="Αποθήκευση αλλαγών" />
        </>
      )}
    </div>
  );
};

export default EditPartner;
